/** Scope of Work — v1 scope-of-work.js, the editor v2 exists for: Daniel's
 * machine can't run v1, so the SOW moves here. Reads come straight from the
 * scope tables (PHASE15 policies); writes are per-record ops through the
 * door (scope_meta / scope_cats / scope_items — the same sections v1's
 * Phase-4A client uses, so both apps see each other's edits).
 *
 * Math is v1 verbatim: scopePropMarkup / scopeItemMarkup / scopeCatTotals
 * (isSub rows skipped; combined mode bypasses labor+material+markup). */
import { http } from "../../lib/http";
import { moneyExact } from "../../lib/money";
import { API_BASE, SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import type { RecOp } from "../../lib/outbox";

// ---- v1 vocabulary -----------------------------------------------------------

/** v1 SCOPE_SECTIONS, verbatim (woodwork is a special calculator in v1 —
 * its cats still render here generically, its calculator stays v1 for now). */
export const SOW_SECTIONS: [string, string][] = [
  ["exteriors", "Exteriors"], ["interiors", "Interiors"],
  // mid-rehab additions the seller/GC agrees to AFTER the scope was set —
  // kept as their own section so the original budget stays honest
  // (Tom 2026-08-20: "a separate like exterior interior, one that will be
  // called Change order", placed right of Interiors per his ask)
  ["changeorder", "Change Order"],
  ["mep", "MEP"], ["other", "Other"], ["adders", "Adders"],
  ["woodwork", "Wood Work"],
];

/** v1 SCOPE_UNITS (scope-of-work.js) — the SOW's own unit list, NOT the material
 * catalog units. Must match v1 exactly so editing a v1 item never drops its unit. */
export const SOW_UNITS = ["each", "sqft", "lf", "cf", "sf", "lot", "day", "hour"];

/** v1 SCOPE_ROOMS (scope-of-work.js) — room presets for the "Add Room" modal. */
export const SOW_ROOMS = [
  "Kitchen", "Living Room", "Dining Room", "Master Bedroom", "Bedroom 2", "Bedroom 3",
  "Bathroom 1", "Bathroom 2", "Laundry", "Hallway", "Garage", "Exterior", "Basement", "Attic",
];

export interface SowMeta {
  activeSection: string;
  markupMode: "inherit" | "custom" | "off";
  markupPct: number;
  defaultMarkup: number;
  /** unknown v1 meta keys (exp collapse-state, …) preserved on round-trips —
   * scope_meta is a whole-value PUT, so dropping them would wipe v1 state */
  raw: Record<string, unknown>;
}

export interface SowCat {
  id: string;
  name: string;
  section: string;
  order: number;
  followUp: boolean;
  /** v1 cat.note — the follow-up note shown in the header banner. */
  note: string;
}

export interface SowItem {
  id: string;
  catId: string;
  name: string;
  qty: number;
  unit: string;
  labor: number;
  material: number;
  markupMode: "inherit" | "custom" | "off";
  markupPct: number;
  useCombined: boolean;
  combined: number;
  /** v1 item.desc — the description shown on the row (v2 previously mis-read this
   * as `notes`, so every v1-authored item showed a blank description). */
  desc: string;
  /** v1 item.laborNotes / materialNotes — the two small note fields in the editor. */
  laborNotes: string;
  materialNotes: string;
  /** v1 item.photos — reference photos (filenames / data refs). */
  photos: string[];
  order: number;
  isSub: boolean;
  /** unknown v1 keys (mats, matName…) preserved for round-trips */
  raw: Record<string, unknown>;
}

export interface SowState {
  meta: SowMeta;
  cats: SowCat[];
  items: SowItem[];
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const num = (v: unknown): number => {
  const f = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(f) ? f : 0;
};
const mode = (v: unknown): "inherit" | "custom" | "off" => (v === "custom" ? "custom" : v === "off" ? "off" : "inherit");

/** v1 scopeSeedObj defaults. */
export const sowSeedMeta = (): SowMeta => ({ activeSection: "interiors", markupMode: "inherit", markupPct: 0, defaultMarkup: 10, raw: {} });

export function narrowSowMeta(raw: unknown): SowMeta {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return sowSeedMeta();
  const o = raw as Record<string, unknown>;
  return {
    activeSection: str(o["activeSection"]) || "interiors",
    markupMode: mode(o["markupMode"]),
    markupPct: num(o["markupPct"]),
    defaultMarkup: o["defaultMarkup"] === undefined ? 10 : num(o["defaultMarkup"]),
    raw: o,
  };
}

export function narrowSowCat(raw: unknown): SowCat | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = str(o["id"]).trim();
  const name = str(o["name"]).trim();
  if (!id || !name) return null;
  return { id, name, section: str(o["section"]) || "interiors", order: num(o["order"]), followUp: o["followUp"] === true, note: str(o["note"]) };
}

export function narrowSowItem(raw: unknown): SowItem | null {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const id = str(o["id"]).trim();
  const name = str(o["name"]).trim();
  if (!id || !name) return null;
  return {
    id,
    catId: str(o["catId"]).trim(),
    name,
    qty: num(o["qty"]),
    unit: str(o["unit"]) || "each",
    labor: num(o["labor"]),
    material: num(o["material"]),
    markupMode: mode(o["markupMode"]),
    markupPct: num(o["markupPct"]),
    useCombined: o["useCombined"] === true,
    combined: num(o["combined"]),
    // v1 stores the description as `desc`; fall back to a legacy v2 `notes` value
    // so nothing typed under the old field is lost.
    desc: str(o["desc"]) || str(o["notes"]),
    laborNotes: str(o["laborNotes"]),
    materialNotes: str(o["materialNotes"]),
    photos: Array.isArray(o["photos"]) ? (o["photos"] as unknown[]).filter((p): p is string => typeof p === "string") : [],
    order: num(o["order"]),
    isSub: o["isSub"] === true,
    raw: o,
  };
}

// ---- v1 math, verbatim ---------------------------------------------------------

/** v1 scopePropMarkup. */
export const propMarkup = (m: SowMeta): number =>
  m.markupMode === "custom" ? m.markupPct || 0 : m.markupMode === "off" ? 0 : m.defaultMarkup;

/** v1 scopeItemMarkup. */
export const itemMarkup = (it: SowItem, m: SowMeta): number =>
  it.markupMode === "custom" ? it.markupPct || 0 : it.markupMode === "off" ? 0 : propMarkup(m);

export interface CatTotals {
  labor: number;
  matBase: number;
  material: number;
  markup: number;
  combined: number;
  total: number;
}

/** v1 scopeCatTotals: isSub rows skipped; combined bypasses labor/material. */
export function catTotals(items: SowItem[], catId: string, m: SowMeta): CatTotals {
  let labor = 0;
  let matBase = 0;
  let matAfter = 0;
  let comb = 0;
  for (const it of items) {
    if (it.catId !== catId || it.isSub) continue;
    const q = it.qty || 0;
    if (it.useCombined && it.combined) {
      comb += q * (it.combined || 0);
    } else {
      labor += q * (it.labor || 0);
      const b = q * (it.material || 0);
      const mk = itemMarkup(it, m);
      matBase += b;
      matAfter += b * (1 + mk / 100);
    }
  }
  return { labor, matBase, material: matAfter, markup: matAfter - matBase, combined: comb, total: labor + matAfter + comb };
}

/** v1 scopeSectionTotal. */
export const sectionTotal = (s: SowState, section: string): number =>
  s.cats.filter((c) => c.section === section).reduce((sum, c) => sum + catTotals(s.items, c.id, s.meta).total, 0);

/** v1 scopeGrand. */
export function grandTotals(s: SowState): { labor: number; material: number; markup: number; total: number } {
  return s.cats.reduce(
    (a, c) => {
      const t = catTotals(s.items, c.id, s.meta);
      return { labor: a.labor + t.labor, material: a.material + t.material, markup: a.markup + t.markup, total: a.total + t.total };
    },
    { labor: 0, material: 0, markup: 0, total: 0 },
  );
}

export const usd = (n: number): string => moneyExact(n);

// ---- reads ---------------------------------------------------------------------

export async function fetchSow(token: string, dealKey: string, signal?: AbortSignal): Promise<SowState | null> {
  const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` };
  const enc = encodeURIComponent(dealKey);
  try {
    const [metaRows, catRows, itemRows] = await Promise.all([
      http<unknown[]>(`${SUPABASE_URL}/rest/v1/rec_scopes?select=nat_key,data&nat_key=eq.${enc}&deleted_at=is.null&limit=1`, { headers, signal, timeoutMs: 20000 }),
      http<unknown[]>(`${SUPABASE_URL}/rest/v1/rec_scope_cats?select=nat_key,data&deal_key=eq.${enc}&deleted_at=is.null&limit=500`, { headers, signal, timeoutMs: 20000 }),
      http<unknown[]>(`${SUPABASE_URL}/rest/v1/rec_scope_items?select=nat_key,data&deal_key=eq.${enc}&deleted_at=is.null&limit=2000`, { headers, signal, timeoutMs: 20000 }),
    ]);
    const metaRow = Array.isArray(metaRows) ? (metaRows[0] as Record<string, unknown> | undefined) : undefined;
    const cats = (Array.isArray(catRows) ? catRows : [])
      .map((r) => narrowSowCat((r as Record<string, unknown>)["data"]))
      .filter((c): c is SowCat => c !== null)
      .sort((a, b) => a.order - b.order);
    const items = (Array.isArray(itemRows) ? itemRows : [])
      .map((r) => narrowSowItem((r as Record<string, unknown>)["data"]))
      .filter((i): i is SowItem => i !== null)
      .sort((a, b) => a.order - b.order);
    return { meta: narrowSowMeta(metaRow?.["data"]), cats, items };
  } catch {
    return null; // transient / policies missing — the section shows the retry note
  }
}

// ---- ops (Phase 15 door shapes) --------------------------------------------------

export const sowId = (prefix: "sc" | "si" | "sm"): string => prefix + Math.random().toString(16).slice(2, 8);

export function opSowMeta(dealKey: string, meta: SowMeta): RecOp {
  // whole-value put: unknown v1 meta keys ride along; the door refuses
  // cats/items and the core strips clipboard/templates — drop them here too.
  const d: Record<string, unknown> = {
    ...meta.raw,
    activeSection: meta.activeSection,
    markupMode: meta.markupMode,
    markupPct: meta.markupPct,
    defaultMarkup: meta.defaultMarkup,
  };
  delete d["cats"];
  delete d["items"];
  delete d["clipboard"];
  delete d["templates"];
  delete d["raw"];
  return { s: "scope_meta", op: "put", k: dealKey, d };
}

export function opSowPutCat(dealKey: string, cat: SowCat): RecOp {
  return { s: "scope_cats", op: "put", k: dealKey + "|" + cat.id, deal: dealKey, d: { id: cat.id, name: cat.name, section: cat.section, order: cat.order, ...(cat.followUp ? { followUp: true } : {}), ...(cat.note ? { note: cat.note } : {}) } };
}

export function opSowDelCat(dealKey: string, catId: string): RecOp {
  return { s: "scope_cats", op: "del", k: dealKey + "|" + catId, deal: dealKey };
}

/** The v2 write door (MIGRATION_CRM2_PHASE15+) rejects a scope_items row whose
 * `d` JSON exceeds 16 KB. Inline base64 (a `data:` URL from the item-photos
 * picker, or a data-URL material thumbnail) can blow that in one shot — and the
 * door rejects the WHOLE item, so a single fat photo would also drop the item's
 * linked materials and price edits ("N changes rejected — kept aside"). Shrink
 * the payload until it fits, sacrificing only the un-persistable bits: first
 * drop `data:` photos (base64 has no home in a 16 KB row — real photos need an
 * upload path), then flatten `data:` material thumbnails (the material's
 * name/price/link survive), then trim the free-text fields. Small http(s)
 * thumbnails and normal scopes are never touched. */
const SCOPE_ITEM_BUDGET = 15000; // margin under the door's 16384
function fitScopeItemD(d: Record<string, unknown>): void {
  const size = (): number => JSON.stringify(d).length;
  if (size() <= SCOPE_ITEM_BUDGET) return;
  // walkthrough check-off photos yield first — evidence thumbnails are the
  // most expendable payload when the row is over budget (2026-08-25)
  const wt = d["wt"];
  if (wt !== null && typeof wt === "object" && !Array.isArray(wt)) {
    const w = wt as Record<string, unknown>;
    const wp = Array.isArray(w["photos"])
      ? (w["photos"] as unknown[]).filter((x): x is string => typeof x === "string") : [];
    // inline dataURL thumbs (pre-2026-09-06) go first, newest-last; stored
    // https URLs are ~110 chars and only yield as a last resort
    const dropOne = (): boolean => {
      let i = wp.length - 1;
      while (i >= 0 && !wp[i]!.startsWith("data:")) i--;
      if (i < 0) i = wp.length - 1;
      if (i < 0) return false;
      wp.splice(i, 1);
      return true;
    };
    for (;;) {
      if (wp.length) w["photos"] = wp;
      else delete w["photos"];
      if (size() <= SCOPE_ITEM_BUDGET || !dropOne()) break;
    }
    if (size() <= SCOPE_ITEM_BUDGET) return;
  }
  const photos = d["photos"];
  if (Array.isArray(photos)) {
    // Photos are added as small downscaled thumbnails, so most fit. Keep as many
    // as the budget allows (drop from the end); a still-too-big one falls away.
    const arr = photos.filter((p): p is string => typeof p === "string");
    for (;;) {
      if (arr.length) d["photos"] = arr;
      else delete d["photos"];
      if (arr.length === 0 || size() <= SCOPE_ITEM_BUDGET) break;
      arr.pop();
    }
    if (size() <= SCOPE_ITEM_BUDGET) return;
  }
  const mats = d["mats"];
  if (Array.isArray(mats)) {
    d["mats"] = mats.map((m) => {
      if (m && typeof m === "object") {
        const o = m as Record<string, unknown>;
        if (typeof o["img"] === "string" && o["img"].startsWith("data:")) return { ...o, img: "" };
      }
      return m;
    });
    if (size() <= SCOPE_ITEM_BUDGET) return;
  }
  for (const key of ["materialNotes", "laborNotes", "desc"]) {
    const v = d[key];
    if (typeof v === "string" && v.length > 300) {
      d[key] = v.slice(0, 300);
      if (size() <= SCOPE_ITEM_BUDGET) return;
    }
  }
}

/** Whole-item put — item edits always send the full object (v1 whole-writes
 * items too; per-field patches on a 15-field row buy nothing). Unknown v1
 * keys ride along from `raw` so cross-app round-trips lose nothing. */
export function opSowPutItem(dealKey: string, it: SowItem): RecOp {
  const d: Record<string, unknown> = {
    ...it.raw,
    id: it.id, catId: it.catId, name: it.name, qty: it.qty, unit: it.unit,
    labor: it.labor, material: it.material, useCombined: it.useCombined,
    combined: it.combined, order: it.order,
    // v1 field names: `desc` (not `notes`) + the two note fields + photos.
    desc: it.desc, laborNotes: it.laborNotes, materialNotes: it.materialNotes,
    photos: it.photos,
  };
  delete d["notes"]; // legacy v2 key — description now lives on `desc`
  if (it.markupMode !== "inherit") d["markupMode"] = it.markupMode;
  if (it.markupPct) d["markupPct"] = it.markupPct;
  if (it.isSub) d["isSub"] = true;
  delete d["raw"];
  fitScopeItemD(d); // never exceed the door's 16 KB scope_items cap
  return { s: "scope_items", op: "put", k: dealKey + "|" + it.id, deal: dealKey, d };
}

export function opSowDelItem(dealKey: string, itemId: string): RecOp {
  return { s: "scope_items", op: "del", k: dealKey + "|" + itemId, deal: dealKey };
}

// ---- linked materials (v1 item.mats[] — the in-item catalog picker) ---------

/** A material linked to a SOW item (v1 item.mats entry). Lives in item.raw.mats
 * so it round-trips to/from v1 untouched; the item's per-unit `material` $ is
 * kept equal to the sum below (v1 _scopeMatRecalc). */
export interface SowMatLink {
  id: string;
  name: string;
  price: number;
  qty: number;
  url?: string;
  img?: string;
}

/** Read an item's linked materials. Prefers the newer `mats[]` array, but falls
 * back to v1's LEGACY single-material fields (matName/matPrice/matUrl/matImg) —
 * the common case for v1-authored scopes, where each item links ONE material and
 * never gets a `mats` array. v1 lazily migrates these in `_scopeMats`; v2 must
 * read them too or every v1-linked material shows no pill/thumbnail. */
export function sowMatsOf(raw: Record<string, unknown> | undefined): SowMatLink[] {
  const arr = raw?.["mats"];
  const out: SowMatLink[] = [];
  if (Array.isArray(arr)) {
    arr.forEach((x, i) => {
      if (x === null || typeof x !== "object") return;
      const o = x as Record<string, unknown>;
      const name = str(o["name"]);
      if (!name) return;
      const url = str(o["url"]);
      const img = str(o["img"]);
      out.push({
        // DETERMINISTIC fallback id ("sm-<index>"; the dash can't collide with
        // stored sm<hex> ids). This runs on EVERY render and the ids are React
        // keys — a random sowId() here re-keyed v1-authored material pills and
        // thumbnails each render, remounting the <img> in a loop (reload →
        // hotlink-block error → hide → repeat): the "first category glitching".
        id: str(o["id"]) || "sm-" + i,
        name,
        price: num(o["price"]),
        qty: num(o["qty"]) || 1,
        ...(url ? { url } : {}),
        ...(img ? { img } : {}),
      });
    });
  }
  if (out.length) return out;
  const matName = str(raw?.["matName"]);
  if (matName) {
    const url = str(raw?.["matUrl"]);
    const img = str(raw?.["matImg"]);
    return [{
      id: "sm-legacy", // deterministic — same React-key reason as above
      name: matName,
      price: num(raw?.["matPrice"]),
      qty: 1,
      ...(url ? { url } : {}),
      ...(img ? { img } : {}),
    }];
  }
  return out;
}

/** v1 scopeMatFill: older items linked a material before its image/link were
 * stored on the item — backfill img/url from the catalog by matching name (exact,
 * then prefix either way, like v1). Returns a new list; never mutates input. */
export function sowMatsBackfill(
  mats: SowMatLink[],
  catalog: { name: string; thumbnail: string | null; website: string | null }[],
): SowMatLink[] {
  if (!mats.length || !catalog.length) return mats;
  return mats.map((m) => {
    if ((m.img && m.url) || !m.name) return m;
    const c =
      catalog.find((x) => x.name === m.name) ??
      catalog.find((x) => x.name && (x.name.indexOf(m.name) === 0 || m.name.indexOf(x.name) === 0));
    if (!c) return m;
    const img = m.img || str(c.thumbnail);
    const url = m.url || str(c.website);
    return { ...m, ...(img ? { img } : {}), ...(url ? { url } : {}) };
  });
}

/** v1 _scopeMatSum: Σ qty·price across linked materials = the item's per-unit
 * material cost. */
export const sowMatsSum = (mats: SowMatLink[]): number =>
  Math.round(mats.reduce((s, m) => s + (m.qty || 0) * (m.price || 0), 0) * 100) / 100;

// ---- walkthrough progress (Tom 2026-08-25: "when im going to a property i
// want to see what the contractor has done already") -------------------------

/** Per-item completion, checked off on site. Lives in item.raw.wt so it rides
 * scope_items untouched by v1's editor and survives round-trips. */
export interface SowWt {
  pct: number;               // 0–100; 100 = done, 0 = not started
  at?: string;               // last change (YYYY-MM-DD)
  note?: string;             // flagged issue ("2 screens backordered")
  /** check-off photos. Since 2026-09-06 these are https URLs into the
   * sow-photos storage bucket (~110 chars each, so "as many as we want");
   * older rows may still hold small inline dataURLs, which keep rendering. */
  photos?: string[];
}

// ---- walkthrough photo storage (2026-09-06, Tom: "as many as we want") -----

/** Shrink a camera photo to a JPEG Blob ≤ `max` px on the long side — real
 * evidence quality (1280px ≈ 150–300 KB), not a 34px thumb. */
export function shrinkPhoto(f: File, max = 1280, quality = 0.82): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width || 1, img.height || 1));
      const w = Math.max(1, Math.round((img.width || 1) * scale));
      const h = Math.max(1, Math.round((img.height || 1) * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { reject(new Error("no canvas")); return; }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), "image/jpeg", quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("not an image")); };
    img.src = url;
  });
}

/** Upload one walkthrough photo; the item then stores only the returned URL.
 * Multipart → raw fetch (the browser sets the boundary). */
export async function uploadWalkthroughPhoto(token: string | null, dealKey: string, itemId: string, blob: Blob): Promise<string> {
  const fd = new FormData();
  fd.append("file", blob, "photo.jpg");
  fd.append("deal", dealKey);
  fd.append("item", itemId);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 60000);
  try {
    const res = await fetch(API_BASE + "/api/sow/photo", {
      method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd, signal: ctl.signal,
    });
    if (!res.ok) {
      let detail = String(res.status);
      try { const j = await res.json(); if (typeof j?.detail === "string") detail = j.detail; } catch { /* status */ }
      throw new Error(detail);
    }
    const j = (await res.json()) as { url?: unknown };
    if (typeof j.url !== "string" || !j.url) throw new Error("no url returned");
    return j.url;
  } finally {
    clearTimeout(t);
  }
}

/** Best-effort: remove the stored file when a photo is taken off an item. */
export async function deleteWalkthroughPhoto(token: string | null, url: string): Promise<void> {
  if (!/^https?:/.test(url)) return; // inline dataURL — nothing stored
  try {
    await fetch(API_BASE + "/api/sow/photo/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ url }),
    });
  } catch { /* the item no longer references it; a stray file is harmless */ }
}

export function sowWtOf(raw: Record<string, unknown> | undefined): SowWt {
  const o = raw?.["wt"];
  if (o === null || o === undefined || typeof o !== "object" || Array.isArray(o)) return { pct: 0 };
  const w = o as Record<string, unknown>;
  const p = typeof w["pct"] === "number" && Number.isFinite(w["pct"]) ? w["pct"] : 0;
  const out: SowWt = { pct: Math.max(0, Math.min(100, Math.round(p))) };
  if (typeof w["at"] === "string" && w["at"]) out.at = w["at"];
  if (typeof w["note"] === "string" && w["note"].trim()) out.note = w["note"].trim();
  if (Array.isArray(w["photos"])) {
    const ph = (w["photos"] as unknown[]).filter((x): x is string => typeof x === "string");
    if (ph.length) out.photos = ph;
  }
  return out;
}

export const SOW_MSG = {
  title: "Scope of Work",
  loadFail: "Couldn't load the scope — check the connection and try again.",
  empty: "No scope yet — add the first category to start.",
  emptySection: "No categories in this section yet.",
  v1Note: "Every change here syncs to v1's Scope of Work automatically.",
};
